/**
 * =============================================================================
 * IDS CRM Web - Jobs & Tickets board
 * =============================================================================
 *
 * Gareth, 20 October 2026: team leaders and managers review the current state
 * of open work on a board, one column per stage, each stage its own colour.
 *  - Two tabs: Reactive (tickets) and Scheduled (engineer work and
 *    maintenance). The columns are the open stages switched on in Settings.
 *  - Cards are oldest first. The Reactive tab filters by SLA (all, at risk:
 *    due within 4 hours and not met, breached).
 *  - Nothing is dragged: a card opens a panel at the bottom with its details,
 *    review notes (saved to the ticket or job and the customer timeline) and
 *    an Open button through to the ticket or job page.
 *  - Coordinators see it read only. Finished work stays in the table.
 *  - An Unassigned column comes first on both tabs: open work with no named
 *    engineer (maintenance left with the Planning Owner included) sits there
 *    instead of in its stage column, and the card says which stage it is at.
 *
 * The server decides what is on the board (GET /jobs?view=board, /work-board
 * notes); the Unassigned column is drawn here from each card's engineer.
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ReviewNotes, { NOTE_TYPE_FOR_WORK } from './ReviewNotes';
import BoardAssign from './BoardAssign';
import OpsBoardServiceStatus, { useOnOpsBoard } from './OpsBoardServiceStatus';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { formatDateKey } from '../utils/dates';
import { formatDateTime } from '../utils/format';
import '../pages/OpsBoard.css';
import './WorkBoard.css';

export const BOARD_TABS = [['reactive', 'Reactive'], ['scheduled', 'Scheduled']];
export const SLA_FILTERS = [['all', 'All SLA'], ['due_soon', 'SLA at risk'], ['breached', 'SLA breached']];
const BOARD_TAB_KEY = 'jobsBoard.tab';
const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

const readTab = () => {
  try { return localStorage.getItem(BOARD_TAB_KEY) === 'scheduled' ? 'scheduled' : 'reactive'; } catch (err) { return 'reactive'; }
};
const saveTab = (tab) => { try { localStorage.setItem(BOARD_TAB_KEY, tab); } catch (err) { /* not kept */ } };

export const detailPathFor = (card) => {
  if (card.work_type === 'reactive_ticket') return `/tickets/${card.id}`;
  if (card.work_type === 'scheduled_work') return `/tasks/${card.id}`;
  if (card.work_type === 'scheduled_maintenance') return `/maintenance/${card.id}`;
  return null;
};

const humanise = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** No named engineer yet (Gareth 2026-10-20: the Planning Owner does not count). */
export const isUnassigned = (card) => !String(card.assigned_to_name || '').trim();
export const UNASSIGNED_COLUMN = { value: '__unassigned', label: 'Unassigned', colour: 'pink' };

const stageLabel = (card, columns) => {
  const column = columns.find(col => col.value === card.status);
  return column ? column.label : humanise(card.status);
};

/** "Today", "1 day", "12 days" since the card was created. */
export function ageLabel(createdAt, now = Date.now()) {
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) return '';
  const days = Math.floor((now - created) / DAY_MS);
  if (days <= 0) return 'Today';
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * The SLA chip for a reactive card: breached, or due within the at-risk
 * window ("Due in 3h"), or nothing.
 */
export function slaChip(card, atRiskHours = 4, now = Date.now()) {
  if (card.work_type !== 'reactive_ticket') return null;
  if (card.response_breached || card.resolution_breached) return { tone: 'breached', text: 'SLA breached' };
  const due = card.sla_next_due ? new Date(card.sla_next_due).getTime() : NaN;
  if (!Number.isFinite(due)) return null;
  const left = due - now;
  if (left > 0 && left <= atRiskHours * HOUR_MS) {
    const hours = Math.max(1, Math.round(left / HOUR_MS));
    return { tone: 'due-soon', text: `Due in ${hours}h` };
  }
  return null;
}

function BoardCard({ card, selected, atRiskHours, onSelect, stage, opsColumn }) {
  const chip = slaChip(card, atRiskHours);
  return (
    <button
      type="button"
      className={`ops-card work-card${selected ? ' selected' : ''}`}
      onClick={() => onSelect(card)}
      aria-pressed={selected}
    >
      <span className="ops-card-top">
        <span className="ops-card-ref">{card.ref}</span>
        {card.work_type === 'scheduled_maintenance' && <span className="work-type-badge maintenance">Maintenance</span>}
        {card.work_type === 'scheduled_work' && <span className="work-type-badge engineer">Engineer work</span>}
        {opsColumn && <span className="work-ops-tag" title={`On the Ops Planning board: ${opsColumn}`}>Ops board</span>}
      </span>
      <span className="ops-card-title">{card.title}</span>
      <span className="ops-card-line">
        {[card.customer_name, card.asset_refs || card.asset_ref].filter(Boolean).join(' · ') || 'No customer'}
      </span>
      <span className="ops-card-meta">
        <span className="work-age" title={`Created ${formatDateTime(card.created_at)}`}>{ageLabel(card.created_at)}</span>
        {card.priority && <span className={`ops-priority ops-priority-${card.priority === 'critical' ? 'urgent' : card.priority}`}>{humanise(card.priority)}</span>}
        {chip && <span className={`work-sla work-sla-${chip.tone}`}>{chip.text}</span>}
      </span>
      {stage ? (
        <span className="ops-card-meta ops-card-plan">
          <span className="work-card-stage">{stage}</span>
          {card.assigned_to_planning_group && <span>Maintenance Planning Owner</span>}
        </span>
      ) : card.assigned_to_name && (
        <span className="ops-card-meta ops-card-plan">
          <span>{card.assigned_to_name}</span>
        </span>
      )}
    </button>
  );
}

export function CardPanel({ card, columns, atRiskHours, onClose, canAssign, engineers, onAssigned }) {
  const chip = slaChip(card, atRiskHours);
  const path = detailPathFor(card);
  const facts = [
    ['Customer', card.customer_name],
    ['Robot', card.asset_refs || card.asset_ref],
    ['Stage', stageLabel(card, columns)],
    ['Priority', card.priority ? humanise(card.priority) : null],
    ['Assigned to', card.assigned_to_name || (card.assigned_to_planning_group ? 'No engineer yet (Maintenance Planning Owner)' : 'Nobody yet')],
    ['Opened', `${formatDateTime(card.created_at)} (${ageLabel(card.created_at).toLowerCase()})`],
    ['Work date', card.work_type !== 'reactive_ticket' && card.work_day ? formatDateKey(card.work_day) : null],
    ['Response due', card.sla_response_due ? formatDateTime(card.sla_response_due) : null],
    ['Resolution due', card.sla_resolution_due ? formatDateTime(card.sla_resolution_due) : null],
    ['SLA', chip ? chip.text : null],
    ['Last outcome', card.last_outcome ? humanise(card.last_outcome) : null]
  ].filter(([, value]) => value);

  return (
    <div className={`ops-panel-body work-panel-body${canAssign ? ' with-assign' : ''}`}>
      <section className="ops-panel-details">
        <div className="ops-panel-title">
          <span className="ops-card-ref">{card.ref}</span>
          <h2>{card.title}</h2>
          <div className="work-panel-actions">
            {path && <Link to={path} className="btn-primary btn-small">Open {card.work_type === 'reactive_ticket' ? 'ticket' : 'job'}</Link>}
            <button type="button" className="ops-link-btn" onClick={onClose}>Close</button>
          </div>
        </div>
        <dl className="ops-facts">
          {facts.map(([label, value]) => (
            <React.Fragment key={label}><dt>{label}</dt><dd>{value}</dd></React.Fragment>
          ))}
        </dl>
        {/* On the Ops Planning board, or add it (Gareth 2026-10-21). */}
        <OpsBoardServiceStatus type={NOTE_TYPE_FOR_WORK[card.work_type]} id={card.id} />
      </section>
      {canAssign && (
        <section className="work-panel-assign" aria-label="Engineers">
          <BoardAssign card={card} engineers={engineers} onSaved={onAssigned} />
        </section>
      )}
      <section className="ops-panel-notes">
        <ReviewNotes type={NOTE_TYPE_FOR_WORK[card.work_type]} id={card.id} />
      </section>
    </div>
  );
}

function WorkBoard() {
  const { authFetch } = useAuth();
  const [tab, setTab] = useState(readTab);
  // Each person's own column order, per tab (Unassigned stays first).
  const columnOrder = useColumnOrder(`board_${tab}`);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [myWork, setMyWork] = useState(false);
  const [sla, setSla] = useState('all');
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [engineers, setEngineers] = useState([]);
  // Which of these are on the Ops Planning board, read with each board load.
  const onOpsBoard = useOnOpsBoard(board);
  const searchTimer = useRef(null);
  const canAssign = board?.can_assign === true;

  // Search waits for a pause in typing.
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ view: 'board', board: tab });
      if (tab === 'reactive' && sla !== 'all') params.set('sla', sla);
      if (query) params.set('search', query);
      if (myWork) params.set('my', 'true');
      const response = await authFetch(`/api/v1/jobs?${params}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The board could not be read.');
      setBoard(data);
    } catch (err) {
      setBoard(null);
      setError(err.message === 'Failed to fetch' ? 'The board could not be read. Check your signal and reload the page.' : err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, tab, sla, query, myWork]);

  useEffect(() => { load(); }, [load]);

  // The engineers work can be given to, once, for the assign panel.
  useEffect(() => {
    if (!canAssign) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/v1/tickets/active-engineers');
        const data = await response.json().catch(() => ({}));
        if (!cancelled && response.ok) setEngineers(Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        // Without the list the dropdowns are empty; the board still works.
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, canAssign]);

  // Keep the selection on the fresh copy of its card (it may have changed
  // column after an assign), or drop it when the card has left the board.
  useEffect(() => {
    if (!board || !selected) return;
    const fresh = board.data.find(card => card.work_type === selected.work_type && card.id === selected.id);
    if (!fresh) setSelected(null);
    else if (fresh !== selected) setSelected(fresh);
  }, [board, selected]);

  const byColumn = useMemo(() => {
    const groups = new Map();
    for (const card of board?.data || []) {
      const key = isUnassigned(card) ? UNASSIGNED_COLUMN.value : card.status;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(card);
    }
    return groups;
  }, [board]);

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    saveTab(next);
    setSelected(null);
    setSla('all');
  };

  const atRiskHours = board?.sla_at_risk_hours || 4;
  const arrangedColumns = board ? columnOrder.arrange(board.columns, col => col.value) : [];
  const arrangedKeys = arrangedColumns.map(col => col.value);

  return (
    <div className="work-board">
      <div className="work-board-bar">
        <div className="work-board-tabs" role="tablist" aria-label="Board">
          {BOARD_TABS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              className={tab === value ? 'active' : ''}
              onClick={() => switchTab(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="work-board-filters">
          {/* The SLA filter is Reactive only. Its space is kept (empty) on
              Scheduled so nothing shared moves between tabs, and it sits left
              of the search so search and My work stay together (Gareth 2026-10-20). */}
          <div className="work-board-sla-slot">
            {tab === 'reactive' && (
              <select aria-label="SLA" value={sla} onChange={(e) => setSla(e.target.value)}>
                {SLA_FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            )}
          </div>
          <input
            type="search"
            placeholder="Search ref, customer or robot"
            aria-label="Search the board"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="work-board-check">
            <input type="checkbox" checked={myWork} onChange={(e) => setMyWork(e.target.checked)} />
            My work
          </label>
        </div>
      </div>
      <p className="work-board-count ops-muted">
        {board
          ? <>{board.total} open, oldest first{board.capped ? ` (showing the oldest ${board.data.length})` : ''}{board.can_add_notes ? '' : ' · read only'}</>
          : '\u00a0'}
        {board && columnOrder.customised && (
          <> · <button type="button" className="ops-link-btn work-board-reset-order" onClick={columnOrder.reset}>Usual column order</button></>
        )}
      </p>

      {columnOrder.error && <p className="ops-error">{columnOrder.error}</p>}
      {error && <p className="ops-error">{error}</p>}
      {loading && !board && <p className="ops-muted">Loading the board…</p>}

      {board && (
        <div className="ops-board work-board-columns" role="region" aria-label={`${tab === 'reactive' ? 'Reactive' : 'Scheduled'} board`}>
          {[UNASSIGNED_COLUMN, ...arrangedColumns].map(col => {
            const cards = byColumn.get(col.value) || [];
            const showStage = col.value === UNASSIGNED_COLUMN.value;
            // Unassigned stays first; every other column can be dragged by its header.
            const movable = !showStage;
            const dropClass = movable && columnOrder.over === col.value && columnOrder.dragging !== col.value ? ' column-drop-target' : '';
            return (
              <section
                key={col.value}
                className={`ops-column ops-colour-${col.colour}${dropClass}${columnOrder.dragging === col.value ? ' column-dragging' : ''}`}
                aria-label={col.label}
                onDragOver={movable ? (event) => columnOrder.dragOver(event, col.value) : undefined}
                onDrop={movable ? (event) => columnOrder.drop(event, col.value, arrangedKeys) : undefined}
              >
                <header className={`ops-column-head${movable ? ' column-head-movable' : ''}`} {...(movable ? columnOrder.headerProps(col.value) : {})}>
                  <h2>{col.label}</h2>
                  <span className="ops-count">{cards.length}</span>
                </header>
                <div className="ops-column-cards">
                  {cards.map(card => (
                    <BoardCard
                      key={`${card.work_type}-${card.id}`}
                      card={card}
                      atRiskHours={atRiskHours}
                      stage={showStage ? stageLabel(card, board.columns) : null}
                      selected={selected && selected.id === card.id && selected.work_type === card.work_type}
                      onSelect={setSelected}
                      opsColumn={onOpsBoard.get(`${NOTE_TYPE_FOR_WORK[card.work_type]}:${card.id}`)}
                    />
                  ))}
                  {cards.length === 0 && <p className="ops-empty">Nothing here</p>}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {board && (
        <div className="ops-panel work-board-panel">
          <div className="ops-panel-bar">
            <strong>{selected ? 'Details and review notes' : 'Details and review notes'}</strong>
          </div>
          {selected ? (
            <CardPanel
              card={selected}
              columns={board.columns}
              atRiskHours={atRiskHours}
              onClose={() => setSelected(null)}
              canAssign={canAssign}
              engineers={engineers}
              onAssigned={load}
            />
          ) : (
            <div className="ops-panel-body"><p className="ops-muted">Click a card to see its details and review notes.</p></div>
          )}
        </div>
      )}
    </div>
  );
}

export default WorkBoard;
